import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { ContentPreview, ThemeScope, TourStepPreview } from './previews.jsx';
import { TourFeatureFlag } from './FeatureFlagAttach.jsx';
import { CopyTourButton } from './CopyTour.jsx';
import {
  EyeIcon,
  EyeOffIcon,
  CheckIcon,
  RefreshIcon,
  DeleteIcon,
} from '../components/icons';

/**
 * Tour drill-down — read-only snapshot of one tour's full config.
 *
 * The authoring UI stays in the admin overlay; this page is for
 * auditing "what's published right now", "which elements does step
 * 3 target", "did this tour's URL patterns change between v2 and
 * v3", etc.  Mounts under /projects/<slug>/tours/<tour_id>.
 *
 * Fetches the tour (with steps + URL patterns), plus the version
 * list so you can hop to a prior snapshot without leaving the page.
 */
export function TourDetail() {
  const { tour_id: tourId } = useParams();
  const { getToken } = useAuth();
  const { project } = useOutletContext() || {};
  const api = useMemo(
    () => createApiClient(getToken, { projectId: project?.project_id || null }),
    [getToken, project?.project_id],
  );

  const [tour, setTour] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, v] = await Promise.all([
        api(`/v1/admin/tours/${encodeURIComponent(tourId)}`),
        api(`/v1/admin/tours/${encodeURIComponent(tourId)}/versions`).catch(() => ({ items: [] })),
      ]);
      setTour(t);
      setVersions(v?.items || v?.versions || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, tourId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="state"><div className="state__title">Loading tour…</div></div>;
  }
  if (error || !tour) {
    return (
      <div className="state state--error">
        <div className="state__title">Couldn't load tour</div>
        <div>{error || `No tour with id ${tourId}`}</div>
      </div>
    );
  }

  return (
    <div className="tour-detail">
      <div style={{ marginBottom: 12 }}>
        <Link to="../tours" className="btn btn--ghost" style={{ padding: 0 }}>← All tours</Link>
      </div>

      <header className="card tour-detail__header">
        <div>
          <div className="tour-detail__id"><code>{tour.tour_id}</code></div>
          <h2 style={{ margin: '4px 0 6px' }}>{tour.title}</h2>
          {tour.description && (
            <p style={{ margin: 0, color: 'var(--text-3)', fontSize: 13.5 }}>
              {tour.description}
            </p>
          )}
        </div>
        <div className="tour-detail__meta">
          <StatusBadge status={tour.status} />
          <span className="badge badge--default">v{tour.version}</span>
          {tour.admin_only && <span className="badge badge--admin">admin only</span>}
          {tour.require_all_targets && (
            <span className="badge badge--viewer" title="SDK only starts the tour if every step's target exists on the page">
              strict targets
            </span>
          )}
          <StatusActions
            tour={tour}
            api={api}
            onChanged={(updated) => setTour(updated)}
          />
          <CopyTourButton tour={tour} api={api} />
        </div>
      </header>

      <div className="tour-detail__grid">
        <section className="card">
          <h3 className="card__title">URL patterns</h3>
          <p className="card__body">
            The tour is considered for any page whose path matches one
            of these. Pattern syntax is basic prefix/glob; regex
            patterns are flagged explicitly.
          </p>
          {(tour.url_patterns || []).length === 0 ? (
            <div className="list__meta" style={{ marginTop: 8 }}>
              No patterns — the tour won't auto-fire anywhere.
            </div>
          ) : (
            <ul className="list" style={{ marginTop: 10 }}>
              {tour.url_patterns.map((p, i) => (
                <li key={i} className="list__row list__row--muted">
                  <div className="list__primary">
                    <span className="list__name"><code>{p.pattern}</code></span>
                    <span className="list__meta">
                      {p.is_regex ? 'regex' : 'glob'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card">
          <h3 className="card__title">Behaviour</h3>
          <dl className="tour-detail__dl">
            <dt>Priority</dt><dd>{tour.priority ?? 0}</dd>
            <dt>Show progress</dt><dd>{tour.show_progress ? 'Yes' : 'No'}</dd>
            <dt>Allow restart</dt><dd>{tour.allow_restart ? 'Yes' : 'No'}</dd>
            <dt>Dismiss behaviour</dt><dd><code>{tour.dismiss_behavior || 'hide_session'}</code></dd>
            <dt>Default locale</dt><dd><code>{tour.default_locale || 'en'}</code></dd>
            {tour.feature_flag && (
              <>
                <dt>Feature flag</dt><dd><code>{tour.feature_flag}</code></dd>
              </>
            )}
          </dl>
        </section>
      </div>

      <section className="card" style={{ marginTop: 14 }}>
        <h3 className="card__title">Steps ({tour.steps?.length ?? 0})</h3>
        {!tour.steps?.length ? (
          <div className="list__meta" style={{ marginTop: 8 }}>
            This tour has no steps yet. It might be a stub you haven't
            finished in the overlay.
          </div>
        ) : (
          <ol className="tour-steps">
            {tour.steps.map((s, i) => (
              <StepRow key={s.step_id || i} step={s} index={i} />
            ))}
          </ol>
        )}
      </section>

      <TourFeatureFlag tour={tour} api={api} onChanged={load} />

      {(tour.start_page || tour.finish_page) && (
        <section className="card" style={{ marginTop: 14 }}>
          <h3 className="card__title">Welcome / finish pages</h3>
          <p className="card__body">
            Shown as centered modals before step 1 and after the last
            step.  Both optional; when absent the tour opens straight
            into step 1 and closes silently at the end.
          </p>
          <div className="tour-detail__pages">
            {tour.start_page && <TourPagePreview label="Start page" page={tour.start_page} />}
            {tour.finish_page && <TourPagePreview label="Finish page" page={tour.finish_page} />}
          </div>
        </section>
      )}

      {versions.length > 0 && (
        <section className="card" style={{ marginTop: 14 }}>
          <h3 className="card__title">Version history</h3>
          <p className="card__body">
            Every publish minted an immutable snapshot. Useful when you
            need to confirm "what was live last Tuesday" without
            inferring from audit logs.
          </p>
          <ul className="list" style={{ marginTop: 10 }}>
            {versions.map((v) => (
              <li key={v.version} className="list__row list__row--muted">
                <div className="list__primary">
                  <span className="list__name">v{v.version}</span>
                  <span className="list__meta">
                    published {formatDate(v.published_at || v.created_at)}
                    {v.published_by && <> · by {v.published_by}</>}
                  </span>
                </div>
                {v.version === tour.version && (
                  <span className="badge badge--editor">current</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      <style>{TOUR_CSS}</style>
    </div>
  );
}

function StepRow({ step, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <li className="tour-step">
      <div className="tour-step__marker">{(step.order ?? index) + 1}</div>
      <div className="tour-step__body">
        <div className="tour-step__head">
          <strong>{step.title || <em style={{ color: 'var(--text-3)' }}>untitled step</em>}</strong>
          {step.optional && <span className="badge badge--default">optional</span>}
          {step.position && <span className="badge badge--viewer">{step.position}</span>}
          {step.completion?.type && (
            <span
              className="badge badge--editor"
              title="Interactive step — advances when the user performs the action"
            >
              interactive
            </span>
          )}
          <button
            type="button"
            className="btn btn--ghost tour-step__preview-toggle"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <EyeOffIcon /> : <EyeIcon />}
            {open ? 'Hide preview' : 'Preview'}
          </button>
        </div>
        {step.body && <p style={{ margin: '6px 0 10px', color: 'var(--text-2)' }}>{step.body}</p>}
        <div className="tour-step__meta">
          {step.element_target?.selector && (
            <span>
              target <code>{step.element_target.selector}</code>
              {step.element_target?.fallbacks?.length ? (
                <> · {step.element_target.fallbacks.length} fallback{step.element_target.fallbacks.length === 1 ? '' : 's'}</>
              ) : null}
            </span>
          )}
          {step.cta_text && <span>CTA "{step.cta_text}"</span>}
          {step.cta_url && <span><code>{step.cta_url}</code></span>}
          {step.completion?.type && (
            <span>
              waits for <code>{describeCompletion(step.completion)}</code>
              {step.completion.timeout_ms ? (
                <> · timeout {Math.round(step.completion.timeout_ms / 1000)}s</>
              ) : null}
              {step.completion.auto_advance === false ? (
                <> · manual advance</>
              ) : null}
            </span>
          )}
        </div>
        {open && (
          <ThemeScope className="tour-step__preview">
            <TourStepPreview step={step} index={index} />
          </ThemeScope>
        )}
      </div>
    </li>
  );
}

function TourPagePreview({ label, page }) {
  // Tour start + finish pages render in the SDK as a centred modal.
  // Feed the page shape through ContentPreview's modal path so we
  // use the same chrome — and the preview picks up the tenant's
  // theme via the surrounding ThemeScope.
  const item = {
    content_type: 'modal',
    title: page.title,
    body: page.body,
    cta_text: page.cta_text,
  };
  return (
    <div className="tour-page-preview">
      <div className="tour-page-preview__label">{label}</div>
      <ThemeScope>
        <ContentPreview item={item} />
      </ThemeScope>
    </div>
  );
}

/**
 * Human-readable summary of a step's interactive completion
 * criterion.  Mirrors the StepCompletion value object on the
 * backend — keep in sync when new types ship.
 */
function describeCompletion(c) {
  if (!c?.type) return '';
  switch (c.type) {
    case 'click_target':
      return 'click on target';
    case 'click_element':
      return c.target?.data_attribute
        ? `click [data-spotlight="${c.target.data_attribute}"]`
        : c.target?.css_selector
          ? `click ${c.target.css_selector}`
          : 'click an element';
    case 'element_appears':
      return c.target?.data_attribute
        ? `[data-spotlight="${c.target.data_attribute}"] to appear`
        : 'an element to appear';
    case 'input_filled':
      return c.value_regex
        ? `input matching /${c.value_regex}/`
        : 'an input to be filled';
    case 'url_matches':
      return `URL ${c.url_is_regex ? 'matching' : '='} ${c.url_pattern || '(any)'}`;
    case 'custom_event':
      return `event "${c.event_name || '(unnamed)'}"`;
    default:
      return c.type;
  }
}

function StatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  const cls = s === 'published' ? 'badge--editor'
    : s === 'draft' ? 'badge--admin'
    : s === 'archived' ? 'badge--default'
    : 'badge--viewer';
  return <span className={`badge ${cls}`}>{status || 'unknown'}</span>;
}

/**
 * Lifecycle buttons that match the tour's current status:
 *   draft     → Publish  (live)        + Archive (hide entirely)
 *   published → Unpublish (back to draft) + Archive
 *   archived  → Restore   (back to draft)
 * Calling any of these PATCHes the tour and propagates the
 * fresh row up via onChanged so the parent re-renders without a
 * full reload.
 */
function StatusActions({ tour, api, onChanged }) {
  const [confirmNode, ask] = useConfirm();
  const [busy, setBusy] = useState(null);
  const status = (tour.status || '').toLowerCase();

  async function publish() {
    setBusy('publish');
    try {
      const resp = await api(`/v1/admin/tours/${encodeURIComponent(tour.tour_id)}/publish`, { method: 'POST' });
      onChanged(resp);
    } finally { setBusy(null); }
  }

  async function unpublish() {
    setBusy('unpublish');
    try {
      const resp = await api(`/v1/admin/tours/${encodeURIComponent(tour.tour_id)}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'draft' }),
      });
      onChanged(resp);
    } finally { setBusy(null); }
  }

  function archive() {
    ask({
      title: `Archive "${tour.title}"?`,
      body: 'Users stop seeing it immediately; you can restore later.',
      confirmLabel: 'Archive',
      danger: true,
      runOnConfirm: async () => {
        setBusy('archive');
        try {
          const resp = await api(`/v1/admin/tours/${encodeURIComponent(tour.tour_id)}/archive`, { method: 'POST' });
          onChanged(resp);
        } finally { setBusy(null); }
      },
    });
  }

  async function restore() {
    setBusy('restore');
    try {
      const resp = await api(`/v1/admin/tours/${encodeURIComponent(tour.tour_id)}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'draft' }),
      });
      onChanged(resp);
    } finally { setBusy(null); }
  }

  return (
    <div style={{ display: 'inline-flex', gap: 6 }}>
      {status === 'draft' && (
        <button type="button" className="btn btn--primary" onClick={publish} disabled={busy !== null}>
          <CheckIcon /> {busy === 'publish' ? 'Publishing…' : 'Publish'}
        </button>
      )}
      {status === 'published' && (
        <button type="button" className="btn btn--ghost" onClick={unpublish} disabled={busy !== null}>
          <EyeOffIcon /> {busy === 'unpublish' ? 'Unpublishing…' : 'Unpublish'}
        </button>
      )}
      {status === 'archived' ? (
        <button type="button" className="btn btn--ghost" onClick={restore} disabled={busy !== null}>
          <RefreshIcon /> {busy === 'restore' ? 'Restoring…' : 'Restore'}
        </button>
      ) : (
        <button type="button" className="btn btn--danger-ghost" onClick={archive} disabled={busy !== null}>
          <DeleteIcon /> {busy === 'archive' ? 'Archiving…' : 'Archive'}
        </button>
      )}
      {confirmNode}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

const TOUR_CSS = `
  .tour-detail { margin-top: 4px; }
  .tour-detail__header {
    display: flex;
    gap: 24px;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .tour-detail__id {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-3);
    letter-spacing: 0.02em;
  }
  .tour-detail__meta {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    align-items: center;
  }
  .tour-detail__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 14px;
    margin-top: 14px;
  }
  .tour-detail__dl {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 8px 14px;
    margin: 10px 0 0;
    font-size: 13px;
  }
  .tour-detail__dl dt {
    color: var(--text-3);
    font-weight: 500;
  }
  .tour-detail__dl dd {
    margin: 0;
    color: var(--text-1);
  }
  .tour-steps {
    list-style: none;
    padding: 0;
    margin: 10px 0 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .tour-step {
    display: flex;
    gap: 12px;
    padding: 10px 12px;
    border-radius: var(--r-md);
    background: var(--surface-1);
  }
  .tour-step__marker {
    flex: 0 0 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--brand-600);
    color: var(--text-inverse);
    font-weight: 700;
    font-size: 12.5px;
    display: grid;
    place-items: center;
  }
  .tour-step__body { flex: 1 1 auto; min-width: 0; }
  .tour-step__head {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
  .tour-step__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    color: var(--text-3);
    font-size: 12.5px;
  }
  .tour-step__head { justify-content: flex-start; }
  .tour-step__preview-toggle {
    margin-left: auto;
    padding: 2px 10px;
    font-size: 12px;
  }
  .tour-step__preview { margin-top: 10px; }
  .tour-detail__pages {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 14px;
    margin-top: 10px;
  }
  .tour-page-preview__label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-3);
    margin-bottom: 6px;
  }
`;
